import { createContext, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useSWR from "swr";
import axios from "axios";
import { useAuthHeader, useAuthUser } from "react-auth-kit";
import {
  NOTIFICATION_ENDPOINT,
  GENERAL_REQUESTS_ENDPOINT,
  EVIDENCE_TEMPLATE_ENDPOINT,
  EVENTTYPE_API,
  MEASUREMENTUNIT_API,
  PERSONAL_API,
  CLIENTS_API,
  LOCATION_API,
  SUPPLY_API,
  SUPPLY_USAGE_API,
  ADDITIONAL_COST_API,
  TEAMS_API,
  EVENTS_API,
  DEVICE_SETUP_API,
  COMPANY_ENDPOINT,
  STATUS_ENDPOINT,
  NEWNESS_ENDPOINT,
  WORKFLOW_ENDPOINT,
  FORM_FIELDS_ENDPOINT,
  DYNAMIC_FORM_ENDPOINT,
  VEHICLE_ENDPOINT,
  ADDITIONAL_NEWNESS_ENDPOINT,
  QUOTATION_SETUP_ENDPOINT,
  QUOTATION_REQUEST_ENDPOINT,
  VEHICLE_TYPE_ENDPOINT,
  WAREHOUSE_ENDPOINT,
  OPERATIONAL_AREA_API,
  STATUS_CHANGE_SETUP_API,
  CONTRACTOR_API,
  RESOLUTION_ENDPOINT,
  PROFILE_ENDPOINT,
  AUTH_ENDPOINT,
  POSITION_ENDPOINT,
  DYNAMIC_FORM_FIELD_VALUE_ENDPOINT,
  PROCEEDING_ENDPOINT,
  EVIDENCE_ENDPOINT,
  CLIENT_TYPE_ENDPOINT,
  ADDITIONAL_COST_TYPE_ENDPOINT,
  PAYMENT_TYPE_ENDPOINT,
  ADDITIONAL_COST_ENDPOINT,
  PAYMENT_TO_TECHNICIAN_ENDPOINT,
  PREORDER_ENDPOINT,
  BILL_ENDPOINT,
  DAILY_ROUTE_ENDPOINT,
  INVENTORY_MOVEMENT_TYPE_ENDPOINT,
  INVENTORY_MOVEMENT_HEADER_ENDPOINT,
  PROVIDER_ENDPOINT,
  REQUEST_TYPE_ENDPOINT,
  REQUEST_GENERAL_NOTIFICATION,
  REPORT_TYPE_ENDPOINT,
  FINISH_CONCEPT_ENDPOINT,
} from "../services/config";
import {
  getClearProviderModel,
  getClearEventTypeModel,
  getClearBasicModel,
  getClearClientModel,
  getClearEventModel,
  getClearWorkflowModel,
  getClearEventFilteringModel,
  getClearStatusChangeModel,
  getClearContractorModel,
  getClearPersonalModel,
  getClearVehicleModel,
  getclearFormFieldModel,
  getClearTeamModel,
  getClearResolutionModel,
  getClearMeasurementUnitModel,
  getClearEventStatusModel,
  getClearSupplyModel,
  getClearAdditionalNewnessModel,
  getClearSupplyUsageModel,
  getClearStatusChangeEventModel,
  getUserData,
  getClearProfileModel,
  getClearUserModel,
  defaultFilteringForEvent,
  defaultFilteringForNewness,
  getClearOperationalAreaModel,
  getClearQuotationSetupModel,
  getClearQuotationRequestModel,
  getClearAdditionalCostTypeModel,
  getClearPaymentTypeModel,
  defaultFilteringForPreOrders,
  getClearEvidenceTemplateModel,
  defaultFilteringForDailyRoutes,
  getClearInventoryMovementType,
  getClearInventoryMovement,
  getClearRequestType,
  getReportSections,
  getClearFinishConcept,
} from "../common/EntitiesManagement";
import localize from "../common/localize";

const GlobalContext = createContext();

const ValidateResponse = (responseReceived) => {
  if (!responseReceived) {
    return { ok: false, data: localize.error_no_response_from_server };
  }

  const responseData = responseReceived?.data;
  return {
    ok: responseData.Ok,
    data: responseData.Data,
  };
};

export const createEntity = async (endpoint, model, headers) => {
  try {
    const newModel = {
      ...model,
      CreatedBy: getUserData()?.UserName,
    };

    const result = await axios.post(endpoint, newModel, {
      ...headers,
      ManagementId: getUserData()?.ManagementId,
    });
    return ValidateResponse(result);
  } catch (error) {
    return { ok: false, data: error.message };
  }
};

export const updateEntity = async (endpoint, model, headers) => {
  try {
    const newModel = {
      ...model,
      CreatedBy: getUserData()?.UserName,
    };
    const result = await axios.put(endpoint, newModel, {
      ...headers,
      ManagementId: getUserData()?.ManagementId,
    });
    return ValidateResponse(result);
  } catch (error) {
    return { ok: false, data: error.message };
  }
};

export const deleteEntity = async (endpoint, headers) => {
  try {
    const result = await axios.delete(endpoint, {
      ...headers,
      ManagementId: getUserData()?.ManagementId,
    });
    return ValidateResponse(result);
  } catch (error) {
    return { ok: false, data: error.message };
  }
};

const ContextProvider = ({ children }) => {
  const auth = useAuthUser();
  const userData = auth();
  const authHeader = useAuthHeader();
  const location = useLocation();
  const [pageIndexMyRequests, setPageIndexMyRequets] = useState(0);
  const [pageIndexRequestAssignedToMe, setPageIndexRequestAssignedToMe] =
    useState(0);
  const [pageIndexRequestType, setPageIndexRequestType] = useState(0);
  const [pageIndexReportType, setPageIndexReportType] = useState(0);
  const [pageIndexBill, setPageIndexBill] = useState(0);
  const [pageIndexOperativeArea, setPageIndexOperativeArea] = useState(0);
  const [pageIndexEventType, setPageIndexEventType] = useState(0);
  const [pageIndexFormFields, setPageIndexFormFields] = useState(0);
  const [pageIndexDynamicForm, setPageIndexDynamicForm] = useState(0);
  const [pageIndexExecutionStatus, setPageIndexExecutionStatus] = useState(0);
  const [pageIndexWorkflow, setPageIndexWorkflow] = useState(0);
  const [pageIndexAdditionalNewness, setPageIndexAdditionalNewness] =
    useState(0);
  const [pageIndexAdditionalCostTypes, setPageIndexAdditionalCostTypes] =
    useState(0);
  const [pageIndexInventoryMovementType, setPageIndexInventoryMovementType] =
    useState(0);
  const [
    pageIndexInventoryMovementHeader,
    setPageIndexInventoryMovementHeader,
  ] = useState(0);
  const [pageIndexDailyRoute, setPageIndexDailyRoute] = useState(0);
  const [pageIndexPreOrders, setPageIndexPreOrders] = useState(0);
  const [pageIndexPaymentType, setPageIndexPaymentType] = useState(0);
  const [pageIndexQuotationSetup, setPageIndexQuotationSetup] = useState(0);
  const [pageIndexQuotationRequest, setPageIndexQuotationRequest] = useState(0);
  const [pageIndexEvents, setPageIndexEvents] = useState(0);
  const [pageIndexNewness, setPageIndexNewness] = useState(0);
  const [pageIndexContractors, setPageIndexContractors] = useState(0);
  const [pageIndexProviders, setPageIndexProviders] = useState(0);
  const [pageIndexPersonal, setPageIndexPersonal] = useState(0);
  const [pageIndexPosition, setPageIndexPosition] = useState(0);
  const [pageIndexTeam, setPageIndexTeam] = useState(0);
  const [pageIndexMeasurementUnit, setPageIndexMeasurementUnit] = useState(0);
  const [pageIndexSupply, setPageIndexSupply] = useState(0);
  const [pageIndexSupplyRequest, setPageIndexSupplyRequest] = useState(0);
  const [pageIndexAdditionalCostRequest, setPageIndexAdditionalCostRequest] = useState(0);
  const [pageIndexClient, setPageIndexClient] = useState(0);
  const [pageIndexResolution, setPageIndexResolution] = useState(0);
  const [pageIndexVehicleType, setPageIndexVehicleType] = useState(0);
  const [pageIndexWarehouse, setPageIndexWarehouse] = useState(0);
  const [pageIndexVehicle, setPageIndexVehicle] = useState(0);
  const [pageIndexProfile, setPageIndexProfile] = useState(0);
  const [pageIndexProceeding, setPageIndexProceeding] = useState(0);
  const [pageIndexEvidenceTemplate, setPageIndexEvidenceTemplate] = useState(0);
  const [pageIndexFinishConcept, setPageIndexFinishConcept] = useState(0);
  const [city, setCity] = useState(null);
  const [cityFilterCode, setCityFilterCode] = useState(null);
  const [municipalityFilterCode, setMunicipalityFilterCode] = useState(null);
  const [filteringEvent, setFilteringEvent] = useState(
    getClearEventFilteringModel()
  );
  const [basicFilterData, setBasicFilterData] = useState([]);
  const [basicFilterOptions, setBasicFilterOptions] = useState([]);
  const basicDefaultFiltering = defaultFilteringForEvent(true);

  const [filterForEvents, setFilterForEvents] = useState(
    defaultFilteringForEvent()
  );
  const [filterForNewness, setFilterForNewness] = useState(
    defaultFilteringForNewness()
  );
  const [filterForDailyRoutes, setFilterForDailyRoutes] = useState(
    defaultFilteringForDailyRoutes()
  );

  const [filterForPreOrders, setFilterForPreOrders] = useState(
    defaultFilteringForPreOrders()
  );

  const getHeadersWithCorrespondingPagination = (
    configValue,
    pageIndex,
    filterData
  ) => {
    const requestConfig = {
      ...configValue,
      headers: {
        ...configValue.headers,
        pagination: pageIndex,
        Filter: filterData?.length > 0 ? JSON.stringify(filterData) : null,
      },
    };

    return requestConfig;
  };

  const route = location.pathname.split("/")[1];

  const authenticationHeader = {
    authenticate: authHeader(),
  };

  const config = {
    headers: {
      ...authenticationHeader,
      ApplicationId: localize.app_name,
      pagination: 0,
      ManagementId: getUserData()?.ManagementId,
    },
  };

  const configLocations = {
    headers: {
      ...authenticationHeader,
      filtering: `{"Type": 1, "ParentCode": null}`,
      ManagementId: getUserData()?.ManagementId,
    },
  };

  const configCities = {
    headers: {
      ...authenticationHeader,
      filtering: `{"Type": 2, "ParentCode": "${city}"}`,
      ManagementId: getUserData()?.ManagementId,
    },
  };

  const configMunicipalities = {
    headers: {
      ...authenticationHeader,
      filtering: `{"Type": 2, "ParentCode": "${cityFilterCode}"}`,
      ManagementId: getUserData()?.ManagementId,
    },
  };

  const configSidewalks = {
    headers: {
      ...authenticationHeader,
      filtering: `{"Type": 2, "ParentCode": "${municipalityFilterCode}"}`,
      ManagementId: getUserData()?.ManagementId,
    },
  };

  const getStates = async () => {
    try {
      const result = await axios.get(LOCATION_API, configLocations);
      const dataToReturn = ValidateResponse(result, true);
      return dataToReturn.data;
    } catch (error) {
      return undefined;
    }
  };

  const getClientTypes = async () => {
    try {
      const result = await axios.get(CLIENT_TYPE_ENDPOINT, configLocations);
      const dataToReturn = ValidateResponse(result, true);
      return dataToReturn?.data;
    } catch (error) {
      return undefined;
    }
  };

  const getCities = async (stateCode) => {
    try {
      const result = await axios.get(LOCATION_API, {
        headers: {
          ...authenticationHeader,
          filtering: `{"Type": 2, "ParentCode": "${stateCode}"}`,
        },
      });
      return ValidateResponse(result);
    } catch (error) {
      return { ok: false, data: error.message };
    }
  };

  const { data: billsData } = useSWR(
    route === "bills"
      ? [
          BILL_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexBill,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: usersData } = useSWR(
    route === "users" ? [AUTH_ENDPOINT, config] : null
  );

  const { data: resolutionData } = useSWR(
    route === "resolutions" || route === "client"
      ? [
          RESOLUTION_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexResolution,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: proceedingData } = useSWR(
    route === "proceedings" || route === "event" || route === "events"
      ? [
          PROCEEDING_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexProceeding,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: newnessData } = useSWR(
    route === "newness"
      ? [
          NEWNESS_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexNewness,
            filterForNewness
          ),
        ]
      : null
  );

  const { data: dailyRouteData } = useSWR(
    route === "dailyRoute"
      ? [
          DAILY_ROUTE_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexDailyRoute,
            filterForDailyRoutes
          ),
        ]
      : null
  );

  const { data: additionalCostTypeData } = useSWR(
    route === "additionalCostType" || route === "additionalCost"
      ? [
          ADDITIONAL_COST_TYPE_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexAdditionalCostTypes,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: inventoryMovementTypeData } = useSWR(
    route === "inventoryMovementType" || route === "InventoryMovement"
      ? [
          INVENTORY_MOVEMENT_TYPE_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexInventoryMovementType,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: requestTypeData } = useSWR(
    route === "requestType"
      ? [
          REQUEST_TYPE_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexRequestType,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: reportTypeData } = useSWR(
    route === "reportType"
      ? [
          `${REPORT_TYPE_ENDPOINT}/${userData.UserName}`,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexReportType,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: inventoryMovementHeaderData } = useSWR(
    route === "inventoryMovement"
      ? [
          INVENTORY_MOVEMENT_HEADER_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexInventoryMovementHeader,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: preOrderData } = useSWR(
    route === "preorder"
      ? [
          PREORDER_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexPreOrders,
            filterForPreOrders
          ),
        ]
      : null
  );

  const { data: paymentTypeData } = useSWR(
    route === "paymentType" || route === "paymentToTechnician"
      ? [
          PAYMENT_TYPE_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexPaymentType,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: additionalNewnessData } = useSWR(
    route === "additionalnewness"
      ? [
          ADDITIONAL_NEWNESS_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexAdditionalNewness,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: quotationSetupData } = useSWR(
    route === "quotationSetup"
      ? [
          QUOTATION_SETUP_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexQuotationSetup,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: quotationRequestData } = useSWR(
    route === "quotationRequest"
      ? [
          QUOTATION_REQUEST_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexQuotationRequest,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: vehicleData } = useSWR(
    route === "vehicles"
      ? [
          VEHICLE_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexVehicle,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: vehicleTypeData } = useSWR(
    route === "vehicles" || route === "vehicletypes"
      ? [
          VEHICLE_TYPE_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexVehicleType,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: evidenceTemplateData } = useSWR(
    route === "evidencetemplates"
      ? [
          EVIDENCE_TEMPLATE_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexEvidenceTemplate,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: finishConceptData } = useSWR(
    route === "finishConcept"
      ? [
          FINISH_CONCEPT_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexFinishConcept,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: warehouseData } = useSWR(
    route === "warehouse" || route === "warehouses"
      ? [
          WAREHOUSE_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexWarehouse,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: workflowData } = useSWR(
    route === "workflow" || route === "eventtype"
      ? [
          WORKFLOW_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexWorkflow,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: statusChangeData } = useSWR(
    route === "workflow" ? [STATUS_CHANGE_SETUP_API, config] : null
  );

  const { data: dynamicFormData } = useSWR(
    route === "dynamicforms" || route === "workflow" || route === "eventtype"
      ? [
          DYNAMIC_FORM_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexDynamicForm,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: formFieldData } = useSWR(
    route === "formfields" || route === "dynamicforms"
      ? [
          FORM_FIELDS_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexFormFields,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: statusData } = useSWR(
    route === "status" || route === "workflow" || route === "events"
      ? [
          STATUS_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexExecutionStatus,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: eventTypeData } = useSWR(
    route === "eventtype" || route === "events"
      ? [
          EVENTTYPE_API,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexEventType,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: operationalAreaData } = useSWR(
    route === "operational-area" || route === "eventtype"
      ? [
          OPERATIONAL_AREA_API,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexOperativeArea,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: personalData } = useSWR(
    route === "users" ||
      route === "vehicles" ||
      route === "personal" ||
      route === "teams" ||
      route === "events" ||
      route === "dailyRoute"
      ? [
          PERSONAL_API,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexPersonal,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: clientsData } = useSWR(
    route === "client" || route === "events"
      ? [
          CLIENTS_API,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexClient,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: contractorsData } = useSWR(
    route === "contractor" || route === "events" || route === "personal"
      ? [
          CONTRACTOR_API,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexContractors,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: providersData } = useSWR(
    route === "provider" || route === "supply"
      ? [
          PROVIDER_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexProviders,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: locations } = useSWR(
    route === "personal" ||
      route === "contractor" ||
      route === "client" ||
      route === "events" ||
      route === "provider"
      ? [LOCATION_API, configLocations]
      : null
  );

  const { data: cities } = useSWR(
    route === "personal" ||
      route === "contractor" ||
      route === "client" ||
      route === "events" ||
      route === "provider"
      ? [LOCATION_API, configCities]
      : null
  );

  const { data: municipalitiesFromCity } = useSWR(
    route === "personal" ||
      route === "client" ||
      route === "events" ||
      route === "provider"
      ? [LOCATION_API, configMunicipalities]
      : null
  );

  const { data: sidewalksFromMunicipality } = useSWR(
    route === "personal" ||
      route === "client" ||
      route === "events" ||
      route === "provider"
      ? [LOCATION_API, configSidewalks]
      : null
  );

  const { data: measurementUnitData } = useSWR(
    route === "measurementunit" || route === "supply" || route === "events"
      ? [
          MEASUREMENTUNIT_API,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexMeasurementUnit,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: positionData } = useSWR(
    route === "position" || route === "personal"
      ? [
          POSITION_ENDPOINT,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexPosition,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: supplyData } = useSWR(
    route === "supply" || route === "events"
      ? [
          SUPPLY_API,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexSupply,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: supplyRequestData } = useSWR(
    route === "supplyrequest"
      ? [
          SUPPLY_USAGE_API,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexSupplyRequest,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: additionalCostRequestData, error: additionalCostRequestError } = useSWR(
    route === "additionalcostrequest"
      ? [
          ADDITIONAL_COST_API,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexAdditionalCostRequest,
            basicFilterData
          ),
        ]
      : null,
    { suspense: false }
  );

  const { data: teamsData } = useSWR(
    route === "teams" || route === "events"
      ? [
          TEAMS_API,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexTeam,
            basicFilterData
          ),
        ]
      : null
  );

  const { data: eventsData } = useSWR(
    route === "events"
      ? [
          EVENTS_API,
          getHeadersWithCorrespondingPagination(
            config,
            pageIndexEvents,
            filterForEvents
          ),
        ]
      : null
  );

  const { data: profileData } = useSWR(
    route === "profiles" || route === "users"
      ? [
          PROFILE_ENDPOINT,
          getHeadersWithCorrespondingPagination(config, pageIndexProfile),
        ]
      : null
  );

  const navigate = useNavigate();

  const userName = userData?.UserName ?? "Undefined";

  const userManagementId = userData?.ManagementId ?? "Undefined";

  const [billModel, setBillModel] = useState(null);

  const [proceedingModel, setProceedingModel] = useState(getClearBasicModel());

  const [userModel, setUserModel] = useState(getClearUserModel());

  const [visible, setVisible] = useState(false);

  const [member, setMember] = useState([]);

  const [teamModel, setTeamModel] = useState(getClearTeamModel());

  const [positionModel, setPositionModel] = useState(getClearBasicModel());

  const [teamMember, setTeamMember] = useState([]);

  const [supplyUsageModel, setSupplyUsageModel] = useState(
    getClearSupplyUsageModel()
  );

  const [resolutionModel, setResolutionModel] = useState(
    getClearResolutionModel()
  );

  const [supplyModel, setSupplyModel] = useState(getClearSupplyModel());

  const [additionalNewnessModel, setAdditionalNewnessModel] = useState(
    getClearAdditionalNewnessModel()
  );

  const [inventoryMovementTypeModel, setInventoryMovementTypeModel] = useState(
    getClearInventoryMovementType()
  );

  const [requestTypeModel, setRequestTypeModel] = useState(
    getClearRequestType()
  );

  const [reportTypeModel, setReportTypeModel] = useState(getReportSections(0));

  const [inventoryMovementHeaderModel, setInventoryMovementHeaderModel] =
    useState(getClearInventoryMovement());

  const [additionalCostTypeModel, setAdditionalCostTypeModel] = useState(
    getClearAdditionalCostTypeModel()
  );

  const [dailyRouteModel, setDailyRouteModel] = useState();

  const [paymentTypeModel, setPaymentTypeModel] = useState(
    getClearPaymentTypeModel()
  );

  const [quotationSetupModel, setQuotationSetupModel] = useState(
    getClearQuotationSetupModel()
  );

  const [quotationRequestModel, setQuotationRequestModel] = useState(
    getClearQuotationRequestModel()
  );

  const [vehicleTypeModel, setVehicleTypeModel] = useState(
    getClearBasicModel()
  );

  const [evidenceTemplateModel, setEvidenceTemplateModel] = useState(
    getClearEvidenceTemplateModel()
  );

  const [finishConceptModel, setFinishConceptModel] = useState(
    getClearFinishConcept()
  );

  const [warehouseModel, setWarehouseModel] = useState(getClearBasicModel());

  const [workflowModel, setWorkflowModel] = useState(getClearWorkflowModel());

  const [operationalAreaModel, setOperationalAreaModel] = useState(
    getClearOperationalAreaModel()
  );

  const [vehicleModel, setVehicleModel] = useState(getClearVehicleModel());

  const [dynamicFormModel, setDynamicFormModel] = useState(
    getClearBasicModel()
  );

  const [formFieldModel, setFormFieldModel] = useState(
    getclearFormFieldModel()
  );

  const [pageModel, setPageModel] = useState({});
  const [statusModel, setStatusModel] = useState(getClearStatusChangeModel());

  const [statusChangeModel, setStatusChangeModel] = useState(
    getClearStatusChangeModel()
  );

  const [statusChangeEventModel, setStatusChangeEventModel] = useState(
    getClearStatusChangeEventModel()
  );

  const [showEditEventModal, setShowEditEventModal] = useState(false);
  const [showAssignEventModal, setShowAssignEventModal] = useState(false);

  const [measurementUnitModel, setMeasurementUnitModel] = useState(
    getClearMeasurementUnitModel()
  );

  const [eventTypeModel, setEventTypeModel] = useState(
    getClearEventTypeModel()
  );

  const [person, setPerson] = useState(getClearPersonalModel());

  const [clientModel, setClientModel] = useState(getClearClientModel());

  const [contractorModel, setContractorModel] = useState(
    getClearContractorModel()
  );

  const [providerModel, setProviderModel] = useState(getClearProviderModel());

  const [profileModules, setProfileModules] = useState([]);

  const [personalModel, setPersonalModel] = useState(getClearPersonalModel());
  const [profileModel, setProfileModel] = useState(getClearProfileModel());

  const [eventModel, setEventModel] = useState(getClearEventModel());

  const [eventForm, setEventForm] = useState(false);

  const [isChecked, setIsChecked] = useState(false);

  const [stateId, setStateId] = useState(clientModel.State?.Id);

  const [cityId, setCityId] = useState(clientModel.City?.Id);

  const [modal, setModal] = useState("");

  const [modalConfirm, setModalConfirm] = useState(false);
  const [selectedPage, setSelectedPage] = useState(0);
  const [dynamicFormPages, setDynamicFormPages] = useState([]);

  const toggleVisibleModalConfirm = () => {
    setModalConfirm(!modalConfirm);
  };

  const toggleVisible = () => {
    if (route === "/eventtype" || route === `/eventtype/${eventTypeModel.Id}`) {
      navigate("/eventtype");
      setEventTypeModel(getClearEventTypeModel());
    }

    if (route === "/personal" || route === `/personal/${person.id}`) {
      navigate("/personal");
      setPerson(getClearPersonalModel());
    }

    if (route === "/client" || route === `/client/${clientModel.Id}`) {
      navigate("/client");
      setClientModel(getClearClientModel());
      setCityId();
      setStateId();
    }

    if (
      route === "/measurementunit" ||
      route === `/measurementunit/${measurementUnitModel.Id}`
    ) {
      navigate("/measurementunit");
      setMeasurementUnitModel(getClearMeasurementUnitModel());
    }

    if (
      route === "/dynamicforms" ||
      route === `/dynamicforms/${dynamicFormModel.Id}`
    ) {
      navigate("/dynamicforms");
      setDynamicFormModel(getClearBasicModel());
    }

    if (
      route === "/formfields" ||
      route === `/formfields/${formFieldModel.Id}`
    ) {
      navigate("/formfields");
      setFormFieldModel(getclearFormFieldModel());
    }

    if (route === "/status" || route === `/status/${statusModel.Id}`) {
      navigate("/status");
      setStatusModel(getClearEventStatusModel());
    }

    if (route === "/supply" || route === `/supply/${supplyModel.Id}`) {
      navigate("/supply");
      setSupplyModel(getClearSupplyModel());
    }

    if (route === "/teams") {
      navigate("/teams");
      setTeamModel(getClearBasicModel());
    } else if (route === `/teams/${teamModel.id}`) {
      navigate(`/teams/${teamModel.id}`);
    }

    if (route === "/position") {
      navigate("/position");
      setTeamModel(getClearBasicModel());
    } else if (route === `/position/${positionModel.id}`) {
      navigate(`/position/${positionModel.id}`);
    }

    setVisible(!visible);
  };

  const getClientResolutions = async (clientId) => {
    const resolutions =
      clientId > 0
        ? await axios.get(`${CLIENTS_API}/${clientId}/resolution`, config)
        : null;

    if (resolutions?.data.Ok) {
      const result = resolutions?.data?.Data;
      return result;
    }

    return null;
  };

  const getEventResolutions = async (eventId) => {
    const resolutions =
      eventId > 0
        ? await axios.get(`${EVENTS_API}/${eventId}/resolution`, config)
        : null;
    if (resolutions?.data.Ok) {
      const result = resolutions?.data?.Data;
      return result;
    }

    return null;
  };

  const [clientResolutions, setClientResolutions] = useState(null);
  const [eventResolutions, setEventResolutions] = useState(null);

  const data = {
    NOTIFICATION_ENDPOINT,
    GENERAL_REQUESTS_ENDPOINT,
    pageIndexMyRequests,
    setPageIndexMyRequets,
    pageIndexRequestAssignedToMe,
    setPageIndexRequestAssignedToMe,
    REQUEST_TYPE_ENDPOINT,
    REQUEST_GENERAL_NOTIFICATION,
    reportTypeModel,
    setReportTypeModel,
    requestTypeModel,
    requestTypeData,
    reportTypeData,
    REPORT_TYPE_ENDPOINT,
    setRequestTypeModel,
    pageIndexRequestType,
    setPageIndexRequestType,
    pageIndexReportType,
    setPageIndexReportType,
    PROVIDER_ENDPOINT,
    providersData,
    providerModel,
    setProviderModel,
    pageIndexProviders,
    setPageIndexProviders,
    DAILY_ROUTE_ENDPOINT,
    billsData,
    pageIndexDailyRoute,
    setPageIndexDailyRoute,
    billModel,
    setBillModel,
    BILL_ENDPOINT,
    pageIndexBill,
    setPageIndexBill,
    basicFilterData,
    setBasicFilterData,
    basicDefaultFiltering,
    basicFilterOptions,
    setBasicFilterOptions,
    filterForPreOrders,
    setFilterForPreOrders,
    filterForDailyRoutes,
    setFilterForDailyRoutes,
    PAYMENT_TO_TECHNICIAN_ENDPOINT,
    ADDITIONAL_COST_ENDPOINT,
    ADDITIONAL_COST_TYPE_ENDPOINT,
    PAYMENT_TYPE_ENDPOINT,
    additionalCostTypeData,
    dailyRouteData,
    preOrderData,
    pageIndexPreOrders,
    setPageIndexPreOrders,
    PREORDER_ENDPOINT,
    paymentTypeData,
    additionalCostTypeModel,
    setAdditionalCostTypeModel,
    dailyRouteModel,
    setDailyRouteModel,
    paymentTypeModel,
    setPaymentTypeModel,
    pageIndexAdditionalCostTypes,
    setPageIndexAdditionalCostTypes,
    pageIndexPaymentType,
    setPageIndexPaymentType,
    EVIDENCE_ENDPOINT,
    proceedingData,
    proceedingModel,
    setProceedingModel,
    pageIndexProceeding,
    setPageIndexProceeding,
    PROCEEDING_ENDPOINT,
    positionData,
    positionModel,
    setPositionModel,
    getClientResolutions,
    getEventResolutions,
    eventResolutions,
    newnessData,
    quotationSetupData,
    quotationRequestData,
    additionalNewnessData,
    additionalNewnessModel,
    setAdditionalNewnessModel,
    vehicleData,
    vehicleModel,
    setVehicleModel,
    vehicleTypeData,
    vehicleTypeModel,
    evidenceTemplateData,
    evidenceTemplateModel,
    setEvidenceTemplateModel,
    finishConceptData,
    finishConceptModel,
    setFinishConceptModel,
    warehouseData,
    warehouseModel,
    setWarehouseModel,
    setVehicleTypeModel,
    setDynamicFormModel,
    dynamicFormData,
    dynamicFormModel,
    setFormFieldModel,
    formFieldModel,
    formFieldData,
    statusModel,
    setStatusModel,
    statusData,
    EVIDENCE_TEMPLATE_ENDPOINT,
    FINISH_CONCEPT_ENDPOINT,
    EVENTS_API,
    EVENTTYPE_API,
    PERSONAL_API,
    CLIENTS_API,
    LOCATION_API,
    STATUS_ENDPOINT,
    NEWNESS_ENDPOINT,
    WORKFLOW_ENDPOINT,
    FORM_FIELDS_ENDPOINT,
    DYNAMIC_FORM_ENDPOINT,
    ADDITIONAL_NEWNESS_ENDPOINT,
    QUOTATION_REQUEST_ENDPOINT,
    QUOTATION_SETUP_ENDPOINT,
    VEHICLE_TYPE_ENDPOINT,
    WAREHOUSE_ENDPOINT,
    OPERATIONAL_AREA_API,
    VEHICLE_ENDPOINT,
    STATUS_CHANGE_SETUP_API,
    CONTRACTOR_API,
    POSITION_ENDPOINT,
    locations,
    visible,
    toggleVisible,
    eventTypeData,
    config,
    personalData,
    person,
    setPerson,
    pageIndexQuotationSetup,
    setPageIndexQuotationSetup,
    pageIndexQuotationRequest,
    setPageIndexQuotationRequest,
    pageIndexOperativeArea,
    setPageIndexOperativeArea,
    pageIndexEventType,
    setPageIndexEventType,
    pageIndexFormFields,
    setPageIndexFormFields,
    pageIndexDynamicForm,
    setPageIndexDynamicForm,
    pageIndexExecutionStatus,
    setPageIndexExecutionStatus,
    pageIndexWorkflow,
    setPageIndexWorkflow,
    pageIndexAdditionalNewness,
    setPageIndexAdditionalNewness,
    pageIndexEvents,
    setPageIndexEvents,
    pageIndexNewness,
    setPageIndexNewness,
    pageIndexContractors,
    setPageIndexContractors,
    pageIndexPersonal,
    setPageIndexPersonal,
    pageIndexPosition,
    setPageIndexPosition,
    pageIndexTeam,
    setPageIndexTeam,
    pageIndexMeasurementUnit,
    setPageIndexMeasurementUnit,
    pageIndexSupply,
    setPageIndexSupply,
    pageIndexSupplyRequest,
    setPageIndexSupplyRequest,
    supplyRequestData,
    pageIndexAdditionalCostRequest,
    setPageIndexAdditionalCostRequest,
    additionalCostRequestData,
    additionalCostRequestError,
    pageIndexClient,
    setPageIndexClient,
    pageIndexResolution,
    setPageIndexResolution,
    pageIndexVehicleType,
    setPageIndexVehicleType,
    pageIndexWarehouse,
    setPageIndexWarehouse,
    pageIndexVehicle,
    setPageIndexVehicle,
    pageIndexProfile,
    setPageIndexProfile,
    pageIndexEvidenceTemplate,
    setPageIndexEvidenceTemplate,
    pageIndexFinishConcept,
    setPageIndexFinishConcept,
    clientsData,
    clientModel,
    setClientModel,
    contractorsData,
    contractorModel,
    setContractorModel,
    city,
    configLocations,
    setCity,
    cities,
    stateId,
    setStateId,
    cityId,
    setCityId,
    measurementUnitData,
    MEASUREMENTUNIT_API,
    measurementUnitModel,
    setMeasurementUnitModel,
    supplyData,
    SUPPLY_API,
    SUPPLY_USAGE_API,
    supplyModel,
    setSupplyModel,
    teamsData,
    TEAMS_API,
    teamModel,
    setTeamModel,
    setMember,
    member,
    eventsData,
    eventModel,
    setEventModel,
    isChecked,
    setIsChecked,
    modal,
    setModal,
    eventForm,
    setEventForm,
    modalConfirm,
    toggleVisibleModalConfirm,
    filteringEvent,
    setFilteringEvent,
    workflowModel,
    setWorkflowModel,
    workflowData,
    operationalAreaModel,
    setOperationalAreaModel,
    operationalAreaData,
    eventTypeModel,
    setEventTypeModel,
    createEntity,
    updateEntity,
    deleteEntity,
    getStates,
    getCities,
    showEditEventModal,
    setShowEditEventModal,
    showAssignEventModal,
    setShowAssignEventModal,
    statusChangeModel,
    setStatusChangeModel,
    statusChangeData,
    cityFilterCode,
    setCityFilterCode,
    municipalityFilterCode,
    setMunicipalityFilterCode,
    municipalitiesFromCity,
    sidewalksFromMunicipality,
    personalModel,
    setPersonalModel,
    quotationSetupModel,
    setQuotationSetupModel,
    quotationRequestModel,
    setQuotationRequestModel,
    teamMember,
    setTeamMember,
    resolutionData,
    resolutionModel,
    setResolutionModel,
    pageModel,
    setPageModel,
    RESOLUTION_ENDPOINT,
    userName,
    userManagementId,
    selectedPage,
    setSelectedPage,
    dynamicFormPages,
    setDynamicFormPages,
    supplyUsageModel,
    setSupplyUsageModel,
    statusChangeEventModel,
    setStatusChangeEventModel,
    clientResolutions,
    DEVICE_SETUP_API,
    COMPANY_ENDPOINT,
    profileData,
    profileModel,
    setProfileModel,
    PROFILE_ENDPOINT,
    profileModules,
    setProfileModules,
    AUTH_ENDPOINT,
    usersData,
    userModel,
    setUserModel,
    DYNAMIC_FORM_FIELD_VALUE_ENDPOINT,
    filterForNewness,
    setFilterForNewness,
    filterForEvents,
    setFilterForEvents,
    getClientTypes,
    getHeadersWithCorrespondingPagination,
    inventoryMovementTypeModel,
    setInventoryMovementTypeModel,
    inventoryMovementTypeData,
    INVENTORY_MOVEMENT_TYPE_ENDPOINT,
    pageIndexInventoryMovementType,
    setPageIndexInventoryMovementType,
    inventoryMovementHeaderModel,
    setInventoryMovementHeaderModel,
    inventoryMovementHeaderData,
    INVENTORY_MOVEMENT_HEADER_ENDPOINT,
    pageIndexInventoryMovementHeader,
    setPageIndexInventoryMovementHeader,
    getHeadersWithCorrespondingPagination,
    basicFilterData,
  };

  return (
    <GlobalContext.Provider value={data}>{children}</GlobalContext.Provider>
  );
};

export { ContextProvider };
export default GlobalContext;
